// src/components/BuildingRow.js
import React, { useMemo } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Button,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import CenterFocusWeakIcon from "@mui/icons-material/CenterFocusWeak";
import DepartmentRow from "./DepartmentRow";
import {
  textFieldSx,
  formatCoords,
  formatArea,
  formatAreaInput,
  coordsToJSON,
  toInt,
  handleCoordinateChange,
  handleCoordinateBlur,
} from "./utils/tableHelpers";
import { useInternalToggle } from "../hooks/useToggle";

export default function BuildingRow({
  building,
  location,
  departmentsByBuilding,
  roomsByDepartment,
  departmentTypes = [],
  roomTypes = [],
  floorTypes = [],
  onSaveBuilding,
  onSaveDepartment,
  onSaveRoom,
  onCancel,
  forceEdit = false,
}) {
  const { isOpen, toggle } = useInternalToggle(false);
  const [editing, setEditing] = React.useState(forceEdit);
  const [editData, setEditData] = React.useState(building);
  const [tempDepartments, setTempDepartments] = React.useState([]);

  const departments = departmentsByBuilding[building.pk_id] || [];

  // Calculate total number of rooms in this building
  const totalRooms = useMemo(() => {
    return departments.reduce((roomCount, department) => {
      const departmentRooms = roomsByDepartment[department.pk_id] || [];
      return roomCount + departmentRooms.length;
    }, 0);
  }, [departments, roomsByDepartment]);

  React.useEffect(() => setEditData(building), [building]);
  React.useEffect(() => {
    if (forceEdit) setEditing(true);
  }, [forceEdit]);

  const handleEdit = () => setEditing(true);
  const handleCancel = () => {
    if (onCancel && building.isNew) {
      onCancel(); // Remove temporary building
    } else {
      setEditData(building);
      setEditing(false);
    }
  };
  const handleSave = () => {
    const dataToSave = {
      ...editData,
      building_coordinates: coordsToJSON(editData.building_coordinates),
    };
    onSaveBuilding?.(dataToSave);
    setEditing(false);
  };
  const handleChange = (field, value) =>
    setEditData((prev) => ({ ...prev, [field]: value }));

  const handleSameAsLocationAddress = () => {
    if (location) {
      setEditData((prev) => ({
        ...prev,
        building_address: location.location_address || "",
        building_address_2: location.location_address_2 || "",
        building_postcode: location.location_postcode || "",
      }));
    }
  };

  // Department handlers
  const handleCreateDepartment = () => {
    const newTempDepartment = {
      tempId: `temp-${Date.now()}`,
      pk_id: null,
      department_name: "",
      building_pk_id: building.pk_id,
      department_area: "",
      accessibility: false,
      isNew: true,
    };
    setTempDepartments((prev) => [...prev, newTempDepartment]);
  };

  const handleSaveTempDepartment = (departmentData) => {
    // Remove temp fields and save
    const { tempId, isNew, ...cleanData } = departmentData;
    onSaveDepartment?.(cleanData);
    // Remove from temp departments
    setTempDepartments((prev) => prev.filter((d) => d.tempId !== tempId));
  };

  const handleCancelTempDepartment = (tempId) => {
    setTempDepartments((prev) => prev.filter((d) => d.tempId !== tempId));
  };

  return (
    <>
      <TableRow
        hover
        onClick={toggle}
        sx={{
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small">
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={editData.building_name || ""}>
              <span
                style={{
                  display: "inline-block",
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {editData.building_name || "Unnamed building"}
              </span>
            </Tooltip>
            {departments.length > 0 && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <CenterFocusWeakIcon sx={{ fontSize: 16, color: "#666" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {departments.length}
                </Typography>
              </Box>
            )}
            {totalRooms > 0 && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <MeetingRoomIcon sx={{ fontSize: 16, color: "#666" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {totalRooms}
                </Typography>
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 6, my: 1, marginLeft: 0 }}>
              <Card
                variant="outlined"
                sx={{ mb: 2, backgroundColor: "#f9fafb" }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary">
                      Building Details
                    </Typography>
                    {!editing ? (
                      <IconButton size="small" onClick={handleEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={handleSave}
                          color="success"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancel}
                          color="error"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {!editing ? (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 3, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                          Name:
                        </Typography>
                        <Typography variant="body2">
                          {editData.building_name || "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Floors:
                        </Typography>
                        <Typography variant="body2">
                          {editData.number_of_floors ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Area:
                        </Typography>
                        <Typography variant="body2">
                          {formatArea(editData.building_area)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Coordinates:
                        </Typography>
                        <Typography variant="body2">
                          {formatCoords(editData.building_coordinates)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary">
                          Address:
                        </Typography>
                        <Typography variant="body2">
                          {editData.building_address || "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="caption" color="text.secondary">
                          Address 2:
                        </Typography>
                        <Typography variant="body2">
                          {editData.building_address_2 || "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                          Postcode:
                        </Typography>
                        <Typography variant="body2">
                          {editData.building_postcode || "-"}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary">
                          Access Details:
                        </Typography>
                        <Typography variant="body2">
                          {editData.building_access_details || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 3, md: 4 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Name:
                        </Typography>
                        <TextField
                          size="small"
                          value={editData.building_name || ""}
                          onChange={(e) =>
                            handleChange("building_name", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Floors:
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={editData.number_of_floors || ""}
                          onChange={(e) =>
                            handleChange(
                              "number_of_floors",
                              toInt(e.target.value),
                            )
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Area (m²):
                        </Typography>
                        <TextField
                          disabled
                          size="small"
                          type="number"
                          value={formatAreaInput(editData.building_area)}
                          onChange={(e) =>
                            handleChange("building_area", toInt(e.target.value))
                          }
                          fullWidth
                          sx={{
                            ...textFieldSx,
                            "& .MuiInputBase-root.Mui-disabled": {
                              backgroundColor: "#f0f0f0", // Light gray
                              // backgroundColor: "#e0e0e0", // Darker gray
                              // backgroundColor: "#d5d5d5", // Even darker
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#666", // Text color for disabled
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Coordinates (lat, lon):
                        </Typography>
                        <TextField
                          size="small"
                          placeholder="lat, lon"
                          defaultValue={formatCoords(
                            building.building_coordinates,
                          )}
                          onChange={(e) => {
                            handleCoordinateChange(e.target.value, (value) =>
                              handleChange("building_coordinates", value),
                            );
                          }}
                          onBlur={(e) => {
                            handleCoordinateBlur(e.target.value, (value) =>
                              handleChange("building_coordinates", value),
                            );
                          }}
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mb: 0.5, display: "block" }}
                            >
                              Address:
                            </Typography>
                            <TextField
                              size="small"
                              value={editData.building_address || ""}
                              onChange={(e) =>
                                handleChange("building_address", e.target.value)
                              }
                              fullWidth
                              sx={textFieldSx}
                            />
                          </Box>
                          {location && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleSameAsLocationAddress}
                              sx={{ mb: 0, whiteSpace: "nowrap" }}
                            >
                              Same as location
                            </Button>
                          )}
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Address 2:
                        </Typography>
                        <TextField
                          size="small"
                          value={editData.building_address_2 || ""}
                          onChange={(e) =>
                            handleChange("building_address_2", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Postcode:
                        </Typography>
                        <TextField
                          size="small"
                          value={editData.building_postcode || ""}
                          onChange={(e) =>
                            handleChange("building_postcode", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Access Details:
                        </Typography>
                        <TextField
                          size="small"
                          value={editData.building_access_details || ""}
                          onChange={(e) =>
                            handleChange(
                              "building_access_details",
                              e.target.value,
                            )
                          }
                          fullWidth
                          rows={2}
                          multiline
                          sx={textFieldSx}
                        />
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Departments
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleCreateDepartment}
                  sx={{ minWidth: "auto", fontSize: "0.75rem" }}
                >
                  Add Department
                </Button>
              </Box>
              {departments.length === 0 && tempDepartments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No departments
                </Typography>
              ) : (
                <Table size="small" aria-label="departments table">
                  <TableBody>
                    {departments.map((d) => (
                      <DepartmentRow
                        key={d.pk_id}
                        department={d}
                        roomsByDepartment={roomsByDepartment}
                        departmentTypes={departmentTypes}
                        roomTypes={roomTypes}
                        floorTypes={floorTypes}
                        onSaveDepartment={onSaveDepartment}
                        onSaveRoom={onSaveRoom}
                      />
                    ))}
                    {tempDepartments.map((d) => (
                      <DepartmentRow
                        key={d.tempId}
                        department={d}
                        roomsByDepartment={{}}
                        departmentTypes={departmentTypes}
                        roomTypes={roomTypes}
                        floorTypes={floorTypes}
                        onSaveDepartment={handleSaveTempDepartment}
                        onSaveRoom={onSaveRoom}
                        forceEdit={true}
                        onCancel={() => handleCancelTempDepartment(d.tempId)}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
